import React, { useState } from "react";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  (window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1"
    ? "http://localhost:5005"
    : "https://api.surat.softvolute.in");

// ============================================================
// ROLE NORMALIZER
// ============================================================

const normalizeRole = (value = "") => {
  return String(value).trim().toLowerCase().replace(/\s+/g, "_");
};

// ============================================================
// LOGIN
// ============================================================

const Login = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // ============================================================
  // FETCH ROLE PERMISSIONS
  // ============================================================

  const fetchRolePermissions = async (token, user) => {
    try {
      const userRole = normalizeRole(
        user?.role || user?.roleKey || user?.roleName || "",
      );

      console.log("Logged in role:", userRole);

      // Admin has automatic full access
      if (userRole === "admin") {
        localStorage.setItem("permissions", JSON.stringify({}));
        return {};
      }

      const res = await fetch(`${API_BASE_URL}/api/roles`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));

        throw new Error(
          errorData?.message || `Unable to load permissions (${res.status})`,
        );
      }

      const roleData = await res.json();

      console.log("Roles received:", roleData);

      const roles = Array.isArray(roleData)
        ? roleData
        : Array.isArray(roleData?.roles)
          ? roleData.roles
          : [];

      const matchedRole = roles.find((role) => {
        const roleKey = normalizeRole(
          role?.roleKey || role?.role || role?.roleName || role?.name || "",
        );

        return roleKey === userRole;
      });

      if (!matchedRole) {
        console.warn(`No saved role permissions found for: ${userRole}`);

        localStorage.setItem("permissions", JSON.stringify({}));

        return {};
      }

      const permissions =
        matchedRole?.permissions && typeof matchedRole.permissions === "object"
          ? matchedRole.permissions
          : {};

      localStorage.setItem("permissions", JSON.stringify(permissions));

      console.log("Permissions saved:", permissions);

      return permissions;
    } catch (err) {
      console.error("Permission fetch error:", err);

      // Do not keep previous user's permissions
      localStorage.setItem("permissions", JSON.stringify({}));

      throw err;
    }
  };

  // ============================================================
  // LOGIN SUBMIT
  // ============================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");
    setLoading(true);

    try {
      // Clear stale data from previous user
      localStorage.removeItem("permissions");
      localStorage.removeItem("role");
      localStorage.removeItem("userRole");

      // --------------------------------------------------------
      // LOGIN API
      // --------------------------------------------------------

      const res = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },

        body: JSON.stringify({
          email: email.trim(),
          password,
        }),
      });

      let data = {};

      try {
        data = await res.json();
      } catch {
        throw new Error("Invalid response from server");
      }

      if (!res.ok) {
        throw new Error(
          data?.message || data?.error || `Login failed (${res.status})`,
        );
      }

      if (!data?.token) {
        throw new Error("Token not received from server");
      }

      if (!data?.user) {
        throw new Error("User information not received from server");
      }

      // --------------------------------------------------------
      // SAVE TOKEN
      // --------------------------------------------------------

      localStorage.setItem("token", data.token);

      // --------------------------------------------------------
      // SAVE USER
      // --------------------------------------------------------

      localStorage.setItem("user", JSON.stringify(data.user));

      // --------------------------------------------------------
      // SAVE ROLE
      // --------------------------------------------------------

      const normalizedRole = normalizeRole(
        data.user?.role || data.user?.roleKey || data.user?.roleName || "",
      );

      if (normalizedRole) {
        localStorage.setItem("role", normalizedRole);
        localStorage.setItem("userRole", normalizedRole);
      }

      // --------------------------------------------------------
      // LOAD PERMISSIONS
      // --------------------------------------------------------

      try {
        await fetchRolePermissions(data.token, data.user);
      } catch (permissionError) {
        console.error("Could not load permissions:", permissionError);

        // Login should still continue
      }

      // --------------------------------------------------------
      // LOGIN SUCCESS
      // --------------------------------------------------------

      onLoginSuccess(data.token, data.user);
    } catch (err) {
      console.error("Login error:", err);

      // Login failed, remove partial auth data
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("role");
      localStorage.removeItem("userRole");
      localStorage.removeItem("permissions");

      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // UI
  // ============================================================

  return (
    <>
      <style>
        {`
          * {
            box-sizing: border-box;
          }

          body {
            margin: 0;
          }

          /* =====================================================
             MAIN LOGIN PAGE
          ===================================================== */

          .login-page {
            min-height: 100vh;
            width: 100%;
            display: flex;
            background: #f7faf9;
            font-family:
              "Inter",
              "Segoe UI",
              Arial,
              sans-serif;
          }

          /* =====================================================
             LEFT SIDE
          ===================================================== */

          .login-left {
            width: 52%;
            min-height: 100vh;
            position: relative;
            overflow: hidden;

            display: flex;
            align-items: center;
            justify-content: center;

            padding: 60px;

            background: linear-gradient(
              135deg,
              #004d40 0%,
              #00695c 48%,
              #00897b 100%
            );
          }

          /* Decorative circle */

          .login-left::before {
            content: "";
            position: absolute;

            width: 520px;
            height: 520px;

            border-radius: 50%;

            background: rgba(255, 255, 255, 0.045);

            top: -180px;
            left: -180px;
          }

          /* Decorative large circle */

          .login-left::after {
            content: "";
            position: absolute;

            width: 700px;
            height: 700px;

            border-radius: 50%;

            border: 1px solid rgba(255, 255, 255, 0.10);

            bottom: -400px;
            right: -250px;
          }

          /* =====================================================
             BRAND CONTENT
          ===================================================== */

          .brand-content {
            position: relative;
            z-index: 2;

            width: 100%;
            max-width: 570px;

            color: #ffffff;
          }

          /* =====================================================
             ICON
          ===================================================== */

          .brand-icon {
            width: 72px;
            height: 72px;

            border-radius: 18px;

            display: flex;
            align-items: center;
            justify-content: center;

            background: rgba(255, 255, 255, 0.12);

            border: 1px solid rgba(255, 255, 255, 0.20);

            margin-bottom: 30px;

            backdrop-filter: blur(10px);

            box-shadow:
              0 10px 30px rgba(0, 0, 0, 0.10);
          }

          .brand-icon svg {
            width: 40px;
            height: 40px;
          }

          /* =====================================================
             TITLE
          ===================================================== */

          .brand-content h1 {
            margin: 0 0 18px;

            font-size: 54px;
            line-height: 1.05;

            font-weight: 750;

            letter-spacing: -2px;

            color: #ffffff;
          }

          .brand-content h1 span {
            display: block;

            color: #b2dfdb;

            font-size: 48px;

            letter-spacing: -1.5px;
          }

          /* =====================================================
             DESCRIPTION
          ===================================================== */

          .brand-description {
            max-width: 540px;

            margin: 0;

            color: rgba(255, 255, 255, 0.88);

            font-size: 16px;
            line-height: 1.8;

            font-weight: 400;
          }

          /* =====================================================
             FEATURE TAGS
          ===================================================== */

          .brand-footer {
            margin-top: 40px;

            display: flex;

            gap: 12px;

            flex-wrap: wrap;
          }

          .brand-tag {
            padding: 10px 17px;

            border-radius: 30px;

            border: 1px solid rgba(255, 255, 255, 0.28);

            background: rgba(255, 255, 255, 0.08);

            color: #ffffff;

            font-size: 12px;

            font-weight: 500;

            backdrop-filter: blur(5px);
          }

          /* =====================================================
             RIGHT SIDE
          ===================================================== */

          .login-right {
            width: 48%;
            min-height: 100vh;

            display: flex;
            align-items: center;
            justify-content: center;

            padding: 40px;

            background: #f7faf9;
          }

          .login-box {
            width: 100%;
            max-width: 430px;
          }

          /* =====================================================
             LOGIN HEADER
          ===================================================== */

          .login-header {
            margin-bottom: 28px;
          }

          .login-header h2 {
            margin: 0 0 9px;

            color: #173b36;

            font-size: 31px;

            font-weight: 700;

            letter-spacing: -0.5px;
          }

          .login-header p {
            margin: 0;

            color: #71807d;

            font-size: 14px;
          }

          /* =====================================================
             LOGIN CARD
          ===================================================== */

          .login-card {
            background: #ffffff;

            border: 1px solid #e2ebe8;

            border-radius: 18px;

            padding: 34px;

            box-shadow:
              0 18px 50px rgba(0, 77, 64, 0.08);
          }

          /* =====================================================
             ERROR
          ===================================================== */

          .error-box {
            display: flex;
            align-items: flex-start;

            gap: 10px;

            padding: 12px 14px;

            margin-bottom: 20px;

            border-radius: 10px;

            background: #fff1f1;

            border: 1px solid #ffd6d6;

            color: #c62828;

            font-size: 13px;

            line-height: 1.4;
          }

          .error-icon {
            flex-shrink: 0;

            width: 18px;
            height: 18px;

            border-radius: 50%;

            background: #c62828;

            color: white;

            display: flex;
            align-items: center;
            justify-content: center;

            font-size: 11px;

            font-weight: bold;
          }

          /* =====================================================
             FORM
          ===================================================== */

          .form-group {
            margin-bottom: 19px;
          }

          .form-label {
            display: block;

            margin-bottom: 8px;

            color: #304945;

            font-size: 13px;

            font-weight: 600;
          }

          .input-wrapper {
            position: relative;
          }

          /* =====================================================
             INPUT ICON
          ===================================================== */

          .input-icon {
            position: absolute;

            left: 15px;
            top: 50%;

            transform: translateY(-50%);

            color: #7f918c;

            display: flex;

            pointer-events: none;
          }

          .input-icon svg {
            width: 19px;
            height: 19px;
          }

          /* =====================================================
             INPUT
          ===================================================== */

          .login-input {
            width: 100%;

            height: 50px;

            padding: 0 15px 0 46px;

            border: 1px solid #dce6e3;

            border-radius: 10px;

            background: #fbfdfc;

            color: #253b37;

            font-size: 14px;

            outline: none;

            transition: all 0.2s ease;
          }

          .login-input::placeholder {
            color: #a0ada9;
          }

          .login-input:hover {
            border-color: #b9cbc6;
          }

          .login-input:focus {
            border-color: #00897b;

            background: #ffffff;

            box-shadow:
              0 0 0 3px rgba(0, 137, 123, 0.10);
          }

          .password-input {
            padding-right: 52px;
          }

          /* =====================================================
             PASSWORD TOGGLE
          ===================================================== */

          .password-toggle {
            position: absolute;

            right: 13px;
            top: 50%;

            transform: translateY(-50%);

            border: none;

            background: transparent;

            cursor: pointer;

            padding: 6px;

            color: #71837e;

            display: flex;
            align-items: center;

            transition: color 0.2s ease;
          }

          .password-toggle:hover {
            color: #00897b;
          }

          .password-toggle svg {
            width: 19px;
            height: 19px;
          }

          /* =====================================================
             LOGIN BUTTON
          ===================================================== */

          .login-button {
            width: 100%;

            height: 52px;

            margin-top: 8px;

            border: none;

            border-radius: 10px;

            background: linear-gradient(
              135deg,
              #00695c,
              #00897b
            );

            color: #ffffff;

            font-size: 15px;

            font-weight: 600;

            cursor: pointer;

            box-shadow:
              0 8px 18px rgba(0, 105, 92, 0.22);

            transition: all 0.2s ease;
          }

          .login-button:hover:not(:disabled) {
            transform: translateY(-1px);

            background: linear-gradient(
              135deg,
              #00574d,
              #00796b
            );

            box-shadow:
              0 11px 22px rgba(0, 105, 92, 0.28);
          }

          .login-button:active:not(:disabled) {
            transform: translateY(0);
          }

          .login-button:disabled {
            opacity: 0.7;

            cursor: not-allowed;
          }

          /* =====================================================
             LOADING
          ===================================================== */

          .loading-content {
            display: flex;

            align-items: center;
            justify-content: center;

            gap: 9px;
          }

          .spinner {
            width: 17px;
            height: 17px;

            border: 2px solid rgba(255, 255, 255, 0.35);

            border-top-color: #ffffff;

            border-radius: 50%;

            animation: spin 0.7s linear infinite;
          }

          @keyframes spin {
            to {
              transform: rotate(360deg);
            }
          }

          /* =====================================================
             SECURE LOGIN
          ===================================================== */

          .secure-login {
            display: flex;

            justify-content: center;
            align-items: center;

            gap: 6px;

            margin-top: 18px;

            color: #7f8f8b;

            font-size: 11px;
          }

          .secure-login svg {
            width: 14px;
            height: 14px;
          }

          /* =====================================================
             FOOTER
          ===================================================== */

          .login-footer {
            text-align: center;

            margin-top: 12px;

            color: #9aa6a3;

            font-size: 12px;
          }

          /* =====================================================
             RESPONSIVE - TABLET
          ===================================================== */

          @media (max-width: 900px) {
            .login-left {
              width: 44%;
              padding: 35px;
            }

            .login-right {
              width: 56%;
              padding: 25px;
            }

            .brand-content h1 {
              font-size: 38px;
            }

            .brand-content h1 span {
              font-size: 34px;
            }

            .brand-description {
              font-size: 14px;
            }

            .brand-footer {
              margin-top: 30px;
            }

            .brand-tag {
              font-size: 11px;
              padding: 8px 12px;
            }
          }

          /* =====================================================
             RESPONSIVE - MOBILE
          ===================================================== */

          @media (max-width: 700px) {
            .login-page {
              display: block;
            }

            .login-left {
              width: 100%;

              min-height: 270px;

              padding: 35px 25px;

              align-items: flex-start;
            }

            .brand-content {
              max-width: 100%;
            }

            .brand-icon {
              width: 54px;
              height: 54px;

              border-radius: 14px;

              margin-bottom: 18px;
            }

            .brand-icon svg {
              width: 29px;
              height: 29px;
            }

            .brand-content h1 {
              font-size: 32px;

              margin-bottom: 8px;

              letter-spacing: -1px;
            }

            .brand-content h1 span {
              font-size: 29px;

              letter-spacing: -0.5px;
            }

            .brand-description,
            .brand-footer {
              display: none;
            }

            .login-right {
              width: 100%;

              min-height: calc(100vh - 270px);

              padding: 30px 20px 40px;

              align-items: flex-start;
            }

            .login-header {
              margin-bottom: 22px;
            }

            .login-header h2 {
              font-size: 27px;
            }

            .login-card {
              padding: 25px 20px;

              border-radius: 15px;
            }
          }

          /* =====================================================
             SMALL MOBILE
          ===================================================== */

          @media (max-width: 400px) {
            .login-left {
              min-height: 240px;

              padding: 28px 20px;
            }

            .brand-content h1 {
              font-size: 29px;
            }

            .brand-content h1 span {
              font-size: 26px;
            }

            .login-right {
              min-height: calc(100vh - 240px);

              padding: 25px 15px 35px;
            }

            .login-card {
              padding: 22px 17px;
            }
          }
        `}
      </style>

      <div className="login-page">
        {/* ======================================================
            LEFT BRANDING SECTION
        ====================================================== */}

        <div className="login-left">
          <div className="brand-content">
            {/* SYSTEM ICON */}

            <div className="brand-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
              >
                {/* Building */}
                <path d="M3 21V9L12 3L21 9V21" />

                {/* Windows */}
                <path d="M7 21V12H17V21" />

                {/* Door */}
                <path d="M10 21V16H14V21" />

                {/* Small center detail */}
                <path d="M9 8H9.01" />
                <path d="M15 8H15.01" />
              </svg>
            </div>

            {/* TITLE */}

            <h1>
              SVB
              <span>System</span>
            </h1>

            {/* DESCRIPTION */}

            <p className="brand-description">
              Smart Business Management System for managing client quotations,
              vendor operations, income and payments from one centralized
              platform.
            </p>

            {/* FEATURES */}

            <div className="brand-footer">
              <span className="brand-tag">Client Quotes</span>

              <span className="brand-tag">Vendor Management</span>

              <span className="brand-tag">Income & Payments</span>
            </div>
          </div>
        </div>

        {/* ======================================================
            RIGHT LOGIN SECTION
        ====================================================== */}

        <div className="login-right">
          <div className="login-box">
            {/* LOGIN HEADER */}

            <div className="login-header">
              <h2>Welcome Back</h2>

              <p>Sign in to access your SVB System</p>
            </div>

            {/* LOGIN CARD */}

            <div className="login-card">
              {/* ERROR */}

              {error && (
                <div className="error-box">
                  <span className="error-icon">!</span>

                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* ==================================================
                    EMAIL
                ================================================== */}

                <div className="form-group">
                  <label className="form-label">Email Address</label>

                  <div className="input-wrapper">
                    <span className="input-icon">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                      >
                        <rect x="3" y="5" width="18" height="14" rx="2" />

                        <path d="M3 7L12 13L21 7" />
                      </svg>
                    </span>

                    <input
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                      className="login-input"
                    />
                  </div>
                </div>

                {/* ==================================================
                    PASSWORD
                ================================================== */}

                <div className="form-group">
                  <label className="form-label">Password</label>

                  <div className="input-wrapper">
                    <span className="input-icon">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                      >
                        <rect x="4" y="10" width="16" height="11" rx="2" />

                        <path d="M8 10V7C8 4.8 9.8 3 12 3C14.2 3 16 4.8 16 7V10" />
                      </svg>
                    </span>

                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      className="login-input password-input"
                    />

                    {/* SHOW / HIDE */}

                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="password-toggle"
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showPassword ? (
                        /* HIDE ICON */

                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                        >
                          <path d="M3 3L21 21" />

                          <path d="M10.6 10.6A2 2 0 0013.4 13.4" />

                          <path d="M9.9 5.1A10.9 10.9 0 0112 5C17.2 5 20.5 9 21 12C20.8 13.4 20 15 18.8 16.4" />

                          <path d="M6.1 6.1C4.3 7.4 3.3 9.2 3 12C3.5 15 6.8 19 12 19C13.2 19 14.3 18.8 15.3 18.5" />
                        </svg>
                      ) : (
                        /* SHOW ICON */

                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                        >
                          <path d="M2.5 12S6 5 12 5s9.5 7 9.5 7S18 19 12 19s-9.5-7-9.5-7Z" />

                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {/* ==================================================
                    LOGIN BUTTON
                ================================================== */}

                <button
                  type="submit"
                  className="login-button"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="loading-content">
                      <span className="spinner"></span>
                      Signing in...
                    </span>
                  ) : (
                    "Sign In"
                  )}
                </button>
              </form>
            </div>

            {/* ==================================================
                SECURE LOGIN
            ================================================== */}

            <div className="secure-login">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
              >
                <rect x="5" y="10" width="14" height="10" rx="2" />

                <path d="M8 10V7C8 4.8 9.8 3 12 3C14.2 3 16 4.8 16 7V10" />
              </svg>
              Secure login
            </div>

            {/* ==================================================
                FOOTER
            ================================================== */}

            <div className="login-footer">
              © {new Date().getFullYear()} SVB System
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
